/**
 * Client HTTP pour l'API NestJS VVS — porté depuis apps/web/lib/api-client.ts
 * (Next.js) vers ce SPA Vite/React. Contrat inchangé : VVS_MODELE_DONNEES.md
 * §11 (base `/api/v1`, erreurs RFC 7807, montants exposés en objet monétaire,
 * pagination par curseur pour les listings, `Idempotency-Key` obligatoire sur
 * les créations de commande).
 *
 * Ce module n'appelle QUE de vraies routes HTTP : aucune fonction ci-dessous
 * ne renvoie de données simulées. Tant qu'apps/api n'est pas démarré, ces
 * fonctions échoueront à l'appel (fetch en erreur réseau ou 404) — c'est le
 * comportement honnête attendu, plutôt que de masquer l'absence de l'API
 * réelle derrière un mock.
 *
 * Différences avec la version apps/web (les trois seuls changements requis
 * par le porting, voir la tâche de scaffolding de apps/web-vite) :
 *
 *  1. Base URL : TOUJOURS un chemin relatif fixe (`/api/v1`), jamais
 *     `process.env`/`import.meta.env`. En production, nginx sert ce SPA et
 *     proxy `/api/*` vers l'API NestJS en loopback — même origine, pas de
 *     CORS. En dev local, `vite.config.ts` proxy `/api` vers
 *     `http://127.0.0.1:4000` pour reproduire exactement ce comportement.
 *  2. `credentials: "include"` sur CHAQUE fetch() : l'authentification (accès
 *     client/admin, panier invité) passe désormais par des cookies httpOnly
 *     posés directement par apps/api sur /auth/otp/verify, /admin/auth/login,
 *     etc. (changement porté en parallèle côté apps/api) — le navigateur les
 *     attache automatiquement à chaque requête same-origin, ce module ne lit
 *     ni ne stocke plus jamais le jeton lui-même.
 *  3. Rien ici ne lisait `next/headers` (la lecture de cookies côté serveur
 *     vivait dans lib/auth-session.ts / lib/admin-auth-session.ts /
 *     lib/cart-session.ts, volontairement PAS portés tels quels — remplacés
 *     par de simples contextes React côté client, cf. src/lib/auth/*) ; ce
 *     fichier n'avait donc rien à en retirer, confirmé par lecture directe de
 *     la source avant ce port.
 *
 * Effet de bord sur les signatures : les fonctions qui prenaient un
 * `accessToken`/`ctx.accessToken` explicite (parce qu'un Server Component
 * Next.js lisait le cookie httpOnly côté serveur et le retransmettait à la
 * main) gardent EXACTEMENT la même signature ici — c'est un port "presque
 * verbatim" au sens de la tâche de scaffolding, pas une réécriture de ces
 * fonctions. Elles restent appelables telles quelles (le paramètre devient de
 * fait inutile une fois le cookie httpOnly actif : `credentials: "include"`
 * suffit à authentifier la requête, et `accessToken`/`ctx.accessToken`
 * peuvent être omis dès que le futur code appelant n'a plus de jeton en main,
 * cf. `src/lib/auth/*`). Décision volontairement laissée aux ateliers de
 * pages suivants plutôt que retirée ici, pour rester dans le périmètre du
 * porting demandé.
 *
 * Autre écart mineur forcé par le changement de runtime : les options
 * `next: { revalidate, tags }` (hints de cache ISR propres à `fetch()` côté
 * serveur Next.js) n'existent pas dans le `fetch()` du navigateur — absentes
 * du type `RequestInit` du DOM, elles ont donc été retirées (elles n'avaient
 * de toute façon aucun effet possible ici). `cache: RequestCache` (ex.
 * "no-store") est en revanche une option standard du Fetch API et reste
 * inchangée.
 *
 * ATTENTION (reprise telle quelle d'apps/web) : certaines formes ci-dessous ne
 * sont pas illustrées par un exemple JSON exact dans VVS_MODELE_DONNEES.md
 * §11.4 (seuls le devis de livraison, la création de commande — réponse seule
 * — et la fiche produit le sont). Pour les autres endpoints, les types sont
 * inférés à partir des conventions documentées en §11.1/§11.3 ; ils sont
 * signalés par un commentaire "forme inférée" et devront être confirmés/
 * ajustés une fois apps/api en ligne.
 */

const API_PREFIX = "/api/v1";

/**
 * Construit le chemin de requête relatif final : préfixe `/api/v1` + `path`
 * (qui peut déjà contenir une portion `?...` — cf. `attribute=...` répété de
 * `getCatalogProducts`/`getCategoryByPath` ci-dessous) + `searchParams`
 * fusionnés sans écraser une éventuelle query déjà présente dans `path`.
 *
 * Volontairement construit à la main avec `URLSearchParams` plutôt qu'avec le
 * constructeur `URL` (qui exige une base absolue en environnement Node/SSR,
 * contrairement au navigateur où `window.location.origin` ferait l'affaire) :
 * ce module ne doit dépendre d'aucune API navigateur pour rester utilisable
 * telle quelle par un futur outil de prérendu Node (cf. §SEO de la tâche de
 * scaffolding).
 */
function buildRequestPath(
  path: string,
  searchParams?: Record<string, string | number | boolean | undefined>,
): string {
  const [basePath, existingQuery] = path.split("?");
  const query = new URLSearchParams(existingQuery);
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value !== undefined) query.set(key, String(value));
    }
  }
  const qs = query.toString();
  return `${API_PREFIX}${basePath}${qs ? `?${qs}` : ""}`;
}

// ---------------------------------------------------------------------------
// Types communs (§11.1)
// ---------------------------------------------------------------------------

/** Objet monétaire tel qu'exposé par l'API (§11.1). */
export interface MoneyAmount {
  amountMinor: number;
  currency: string;
  minorUnit?: number;
  formatted?: string;
}

/** Erreur au format RFC 7807 (§11.1). */
export interface ApiProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ApiProblemDetails | undefined;

  constructor(status: number, problem: ApiProblemDetails | undefined, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}

export interface Breadcrumb {
  label: string;
  href: string;
}

interface RequestOptions {
  searchParams?: Record<string, string | number | boolean | undefined>;
  cache?: RequestCache;
  /** Requis par l'API sur POST /checkout/orders et POST /payments/* (§11.1). */
  idempotencyKey?: string;
}

async function apiFetch<T>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const requestPath = buildRequestPath(path, options.searchParams);

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;

  const res = await fetch(requestPath, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    cache: options.cache,
    // Cookies httpOnly (accès client/admin, panier invité) attachés
    // automatiquement par le navigateur — cf. commentaire de tête du fichier.
    credentials: "include",
  });

  if (!res.ok) {
    let problem: ApiProblemDetails | undefined;
    try {
      problem = (await res.json()) as ApiProblemDetails;
    } catch {
      problem = undefined;
    }
    throw new ApiError(
      res.status,
      problem,
      problem?.detail ??
        problem?.title ??
        `Échec de la requête API VVS : ${method} ${path} (HTTP ${res.status})`,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// GET /catalog/products
// ---------------------------------------------------------------------------
//
// Forme confirmée par lecture directe de la source de vérité ultime
// (apps/api/src/catalog/catalog.controller.ts, catalog.service.ts,
// catalog.types.ts, dto/product-list-query.dto.ts) plutôt que déduite : le
// listing produit n'a pas d'exemple JSON exact en §11.4.

export type ProductSortOrder = "popularity" | "rating" | "newest" | "price_asc" | "price_desc";

/** Cf. `ProductListQueryDto` côté API. */
export interface ProductListQuery {
  /** Slug simple (ex. "montres") ou chemin complet (ex. "bijoux-montres/montres"). */
  category?: string;
  /** Slug(s) de marque séparés par des virgules (filtre multi-marques). */
  brand?: string;
  tag?: string;
  campaign?: string;
  /**
   * Filtres par attribut, un élément par couple `"code:valeur"` (ex.
   * `["color:noir", "material:or"]`). Des `code` identiques se combinent en
   * OU côté serveur, des `code` différents en ET. Sérialisé en query string
   * sous la forme **répétée** `attribute=code:valeur&attribute=...` — jamais
   * `attribute.code=valeur`.
   */
  attribute?: string[];
  priceMin?: number;
  priceMax?: number;
  inStock?: boolean;
  /** Produits en promotion uniquement (au moins une variante avec un prix barré). */
  onSale?: boolean;
  rating?: number;
  sort?: ProductSortOrder;
  cursor?: string;
  limit?: number;
}

// Nommé `ProductBrandSummary` (et non `BrandSummary`) pour ne pas entrer en collision
// avec l'interface `BrandSummary` (avec `isFeatured`) ajoutée plus bas pour GET
// /catalog/brands — deux formes distinctes de la marque, l'API ne renvoie pas
// `isFeatured` dans le listing produit.
export interface ProductBrandSummary {
  id: string;
  name: string;
  slug: string;
  countryOfOrigin: string | null;
}

export interface CategorySummary {
  id: string;
  name: string;
  slug: string;
  path: string;
}

export interface ProductMedia {
  id: string;
  url: string;
  alt: string | null;
  /** IMAGE le plus souvent ; VIDEO pour la vidéo produit facultative (autoplay). */
  type: "IMAGE" | "VIDEO" | "DOCUMENT";
  isPrimary: boolean;
  position: number;
}

export interface ProductTag {
  code: string;
  label: string;
}

/**
 * Forme exacte de `ProductListItemView` (catalog.types.ts). Attention à deux
 * écarts avec une lecture naïve de ce schéma : le prix est une fourchette
 * (`priceMin`/`priceMax`, un produit peut avoir plusieurs variantes à des prix
 * différents — pas de champ `price` singulier), et il n'y a pas de champ
 * `badges` précalculé côté serveur : les badges (PROMO, NOUVEAU, RUPTURE) se
 * déduisent côté client de `onSale` / `inStock` / `tags`.
 */
export interface ProductListItem {
  id: string;
  slug: string;
  name: string;
  subtitle: string | null;
  brand: ProductBrandSummary | null;
  primaryCategory: CategorySummary | null;
  priceMin: MoneyAmount;
  priceMax: MoneyAmount;
  compareAtPrice: MoneyAmount | null;
  onSale: boolean;
  inStock: boolean;
  rating: { average: number; count: number };
  media: ProductMedia[];
  tags: ProductTag[];
}

export interface ProductListFacetValue {
  attributeValueId: string;
  label: string;
  normalizedValue: string;
  hexColor: string | null;
  count: number;
}

export interface ProductListAttributeFacet {
  code: string;
  label: string;
  values: ProductListFacetValue[];
}

/** Cf. `ProductListFacets` (catalog.types.ts) et VVS_MODELE_DONNEES.md §13. */
export interface ProductListFacets {
  brands: Array<{ id: string; name: string; slug: string; count: number }>;
  price: { min: number; max: number } | null;
  attributes: ProductListAttributeFacet[];
  availability: { inStock: number; outOfStock: number };
  rating: Array<{ minRating: number; count: number }>;
  origins: Array<{ countryOfOrigin: string; count: number }>;
}

/** Forme exacte de `CursorPaginatedResponseDto<ProductListItemView> & { facets }`. */
export interface ProductListResponse {
  items: ProductListItem[];
  nextCursor: string | null;
  facets: ProductListFacets;
}

export function getCatalogProducts(
  query: ProductListQuery = {},
): Promise<ProductListResponse> {
  const { attribute, ...rest } = query;

  // `attribute` doit être répété en query string (`attribute=code:valeur&attribute=...`) :
  // ProductListQueryDto le transforme en tableau de chaînes brutes "code:valeur" (cf.
  // product-list-query.dto.ts `toArray`). `buildRequestPath()` fusionne ensuite les autres
  // paramètres sans écraser cette portion.
  const attributeQueryString = (attribute ?? [])
    .filter((pair) => pair.length > 0)
    .map((pair) => `attribute=${encodeURIComponent(pair)}`)
    .join("&");
  const path = attributeQueryString ? `/catalog/products?${attributeQueryString}` : "/catalog/products";

  return apiFetch<ProductListResponse>("GET", path, undefined, { searchParams: rest });
}

// ---------------------------------------------------------------------------
// GET /catalog/products/:slug
// ---------------------------------------------------------------------------
//
// La forme documentée en §11.4 est globalement correcte mais incomplète :
// lecture directe de `ProductDetailView` (apps/api/src/catalog/catalog.types.ts)
// et de `CatalogService.getProductBySlug()`/`toDetailVariant()` pour combler les
// champs manquants (`media` — indispensable pour la galerie —, `descriptionShort`/
// `descriptionLong`, `precautions`, `nameSuffix`/`isDefault` sur la variante, et
// les champs étendus de `attributes` utiles au sélecteur de variante et aux
// pastilles de couleur).

export interface ProductVariantAttribute {
  code: string;
  label: string;
  /** Libellé de la valeur (ex. "Noir", "42 mm"). */
  value: string;
  normalizedValue: string;
  /** Couleur hexadécimale pour une pastille de couleur, sinon `null`. */
  hexColor: string | null;
  numericValue: number | null;
  unit: string | null;
}

/** Forme exacte de `VariantStockView` (catalog.types.ts). */
export interface ProductVariantStock {
  inStock: boolean;
  quantityAvailable: number;
  lowStock: boolean;
  restockExpectedAt: string | null;
  backInStockAlertAvailable: boolean;
}

export interface ProductVariant {
  id: string;
  sku: string;
  /** Complément de nom propre à la variante (ex. "42 mm — Noir"), ou `null`. */
  nameSuffix: string | null;
  isDefault: boolean;
  attributes: ProductVariantAttribute[];
  price: MoneyAmount;
  compareAtPrice: MoneyAmount | null;
  stock: ProductVariantStock;
}

export interface ProductRatingDistribution {
  "5": number;
  "4": number;
  "3": number;
  "2": number;
  "1": number;
}

/** Toujours présent dans la réponse (moyenne à 0, compteurs à 0 si aucun avis) — ne jamais tester la nullité, tester `count === 0`. */
export interface ProductRating {
  average: number;
  count: number;
  distribution: ProductRatingDistribution;
}

export interface ProductSeo {
  title: string;
  description: string | null;
  canonical: string | null;
  structuredData: Record<string, unknown> | null;
}

export interface ProductDetail {
  id: string;
  slug: string;
  name: string;
  subtitle: string | null;
  descriptionShort: string | null;
  descriptionLong: string | null;
  usageInstructions: string | null;
  precautions: string | null;
  brand: ProductBrandSummary | null;
  breadcrumb: Breadcrumb[];
  tags: ProductTag[];
  variants: ProductVariant[];
  media: ProductMedia[];
  rating: ProductRating;
  relations: { upsell: ProductListItem[]; crossSell: ProductListItem[] };
  seo: ProductSeo;
}

export function getProductBySlug(slug: string): Promise<ProductDetail> {
  return apiFetch<ProductDetail>("GET", `/catalog/products/${encodeURIComponent(slug)}`);
}

// ---------------------------------------------------------------------------
// GET /catalog/products/:slug/related
// ---------------------------------------------------------------------------

export type RelatedProductsType = "UPSELL" | "CROSS_SELL" | "SIMILAR" | "GUIDE_NEXT_STEP" | "REFILL";

export function getRelatedProducts(
  slug: string,
  type?: RelatedProductsType,
): Promise<ProductListItem[]> {
  return apiFetch<ProductListItem[]>(
    "GET",
    `/catalog/products/${encodeURIComponent(slug)}/related`,
    undefined,
    { searchParams: type ? { type } : undefined },
  );
}

// ---------------------------------------------------------------------------
// GET /catalog/categories/:path
// ---------------------------------------------------------------------------
//
// À ne pas confondre avec `getCategoryTree` ci-dessous. Forme confirmée par
// lecture de `CatalogService.getCategoryByPath()` : renvoie
// `{ category, products }`, où `products` a exactement la forme de
// `ProductListResponse` ci-dessus.

export interface CategoryDetail {
  id: string;
  name: string;
  slug: string;
  path: string;
  level: number;
  descriptionShort: string | null;
  descriptionSeo: string | null;
  breadcrumb: Breadcrumb[];
  children: CategorySummary[];
}

export interface CategoryProductsResponse {
  category: CategoryDetail;
  products: ProductListResponse;
}

/**
 * @param path Chemin de catégorie sans slash initial, ex. "bijoux-montres" ou
 *   "bijoux-montres/montres" (segments séparés par `/`, chacun encodé
 *   individuellement pour préserver les `/` significatifs attendus par la
 *   route wildcard `categories/*` côté API).
 */
export function getCategoryByPath(
  path: string,
  query: ProductListQuery = {},
): Promise<CategoryProductsResponse> {
  const { attribute, ...rest } = query;
  const encodedPath = path
    .split("/")
    .filter(Boolean)
    .map(encodeURIComponent)
    .join("/");

  // Même stratagème que `getCatalogProducts()` pour la répétition de `attribute`.
  const attributeQueryString = (attribute ?? [])
    .filter((pair) => pair.length > 0)
    .map((pair) => `attribute=${encodeURIComponent(pair)}`)
    .join("&");
  const basePath = `/catalog/categories/${encodedPath}`;
  const fullPath = attributeQueryString ? `${basePath}?${attributeQueryString}` : basePath;

  return apiFetch<CategoryProductsResponse>("GET", fullPath, undefined, { searchParams: rest });
}

// ---------------------------------------------------------------------------
// GET /catalog/categories/tree
// ---------------------------------------------------------------------------

// Forme confirmée par appel réel à apps/api (CatalogService.getCategoryTree()) :
// l'API renvoie un TABLEAU BRUT de nœuds, pas `{ categories: [...] }`.
export interface CategoryTreeNode {
  id: string;
  name: string;
  slug: string;
  path: string;
  level: number;
  isVisible: boolean;
  productCount: number;
  /** Visuel de rayon, `null` si non renseigné pour cette catégorie. */
  imageUrl: string | null;
  children: CategoryTreeNode[];
}

export function getCategoryTree(): Promise<CategoryTreeNode[]> {
  return apiFetch<CategoryTreeNode[]>("GET", "/catalog/categories/tree");
}

// ---------------------------------------------------------------------------
// POST /auth/otp/request
// ---------------------------------------------------------------------------

export interface OtpRequestPayload {
  /** Numéro au format international, ex. "+2250700000000" (champ réel : `phoneE164`). */
  phoneE164: string;
  /**
   * Obligatoire uniquement pour un numéro encore inconnu de l'API (première
   * connexion) : c'est l'adresse à laquelle le code de vérification est envoyé
   * (canal OTP = email, plus jamais SMS). Optionnel pour un client déjà
   * enregistré — l'API réutilise l'email déjà connu et ignore/refuse cette
   * valeur si elle est fournie et diffère.
   */
  email?: string;
}

// `devCode` n'est présent que si `AUTH_OTP_ENABLED=false` côté API (mode
// dev/test) — en production seul `message` est renvoyé, le code est délivré
// par email.
export interface OtpRequestResponse {
  message: string;
  devCode?: string;
}

export function requestOtp(payload: OtpRequestPayload): Promise<OtpRequestResponse> {
  return apiFetch<OtpRequestResponse>("POST", "/auth/otp/request", payload, { cache: "no-store" });
}

// ---------------------------------------------------------------------------
// Jeton de session panier invité — X-Session-Token
// ---------------------------------------------------------------------------
//
// apps/web (Next.js) posait/lisait son PROPRE cookie httpOnly côté serveur
// (`vvs_session_token`) et le retransmettait en en-tête `X-Session-Token` à
// chaque appel serveur vers l'API. Ici, le navigateur parle DIRECTEMENT à
// l'API (même origine via le proxy nginx/Vite) : `vvs_session_token` devient,
// une fois le changement API-cookies en place, un cookie lisible posé
// directement par apps/api et automatiquement rattaché par
// `credentials: "include"` — plus besoin de le lire/transmettre à la main.
// `sessionApiFetch` garde néanmoins la possibilité de forcer un
// `X-Session-Token`/`Authorization: Bearer` explicite (paramètre `ctx`,
// signature inchangée) pour rester utilisable tel quel par du code appelant
// qui aurait exceptionnellement besoin de le faire — mais aucun appelant de ce
// lot n'a plus de jeton en main à passer (cf. src/lib/auth/*).

/**
 * Contexte d'identité transmis à `sessionApiFetch` : jeton de panier invité
 * (`X-Session-Token`) et/ou jeton d'accès client (`Authorization: Bearer`).
 * Les deux sont acceptés simultanément par `resolveCartContext` côté API : un
 * client authentifié qui a un panier invité en cours (ajouté avant connexion)
 * verra les deux fusionnés (RG-15).
 */
export interface CartAuthContext {
  sessionToken?: string | null;
  accessToken?: string | null;
}

interface SessionRequestOptions {
  searchParams?: Record<string, string | number | boolean | undefined>;
  /** Requis par l'API sur POST /checkout/orders (§11.1). */
  idempotencyKey?: string;
}

async function sessionApiFetch<T>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  body: unknown,
  ctx: CartAuthContext | string | null | undefined,
  options: SessionRequestOptions = {},
): Promise<T> {
  const { sessionToken, accessToken } = typeof ctx === "string" || ctx == null ? { sessionToken: ctx, accessToken: undefined } : ctx;

  const requestPath = buildRequestPath(path, options.searchParams);

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (sessionToken) headers["X-Session-Token"] = sessionToken;
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
  if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;

  const res = await fetch(requestPath, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    cache: "no-store",
    credentials: "include",
  });

  if (!res.ok) {
    let problem: ApiProblemDetails | undefined;
    try {
      problem = (await res.json()) as ApiProblemDetails;
    } catch {
      problem = undefined;
    }
    throw new ApiError(
      res.status,
      problem,
      problem?.detail ??
        problem?.title ??
        `Échec de la requête API VVS : ${method} ${path} (HTTP ${res.status})`,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// GET/POST/PATCH/DELETE /cart · /cart/items · /cart/coupon
// ---------------------------------------------------------------------------
//
// Forme confirmée par lecture directe de apps/api/src/cart/cart.service.ts
// (`CartService.serialize`/`CartResponseDto`) et cart.controller.ts. Le panier
// fonctionne sans compte (commande invité, §3.7) : identifié par
// `X-Session-Token` tant qu'aucun client n'est authentifié.

export interface CartLine {
  id: string;
  variantId: string;
  sku: string;
  productName: string;
  variantName: string | null;
  quantity: number;
  unitPrice: MoneyAmount;
  lineTotal: MoneyAmount;
  priceLockedUntil: string | null;
  priceChanged: boolean;
  stock: { quantityAvailable: number; inStock: boolean };
}

export interface CartCoupon {
  code: string;
  discount: MoneyAmount;
}

export interface Cart {
  id: string;
  status: string;
  currency: string;
  isGuest: boolean;
  /** Non-null en panier invité uniquement : valeur à écrire/rafraîchir dans le cookie `vvs_session_token`. */
  sessionToken: string | null;
  items: CartLine[];
  itemCount: number;
  subtotal: MoneyAmount;
  coupon: CartCoupon | null;
  discountTotal: MoneyAmount;
  total: MoneyAmount;
}

/** `GET /cart` — crée le panier si besoin (get-or-create côté API). */
export function getCart(ctx?: CartAuthContext | string | null): Promise<Cart> {
  return sessionApiFetch<Cart>("GET", "/cart", undefined, ctx);
}

export interface CartItemPayload {
  variantId: string;
  quantity: number;
}

/** `POST /cart/items` — cumule la quantité si la ligne existe déjà (le panier est créé si besoin). */
export function addCartItem(payload: CartItemPayload, ctx?: CartAuthContext | string | null): Promise<Cart> {
  return sessionApiFetch<Cart>("POST", "/cart/items", payload, ctx);
}

/** `PATCH /cart/items` — fixe la quantité absolue d'une ligne déjà présente au panier. */
export function updateCartItem(payload: CartItemPayload, ctx?: CartAuthContext | string | null): Promise<Cart> {
  return sessionApiFetch<Cart>("PATCH", "/cart/items", payload, ctx);
}

/** `DELETE /cart/items?variantId=…`. */
export function removeCartItem(variantId: string, ctx?: CartAuthContext | string | null): Promise<Cart> {
  return sessionApiFetch<Cart>("DELETE", "/cart/items", undefined, ctx, {
    searchParams: { variantId },
  });
}

/** `POST /cart/coupon`. */
export function applyCoupon(code: string, ctx?: CartAuthContext | string | null): Promise<Cart> {
  return sessionApiFetch<Cart>("POST", "/cart/coupon", { code }, ctx);
}

/** `DELETE /cart/coupon`. */
export function removeCoupon(ctx?: CartAuthContext | string | null): Promise<Cart> {
  return sessionApiFetch<Cart>("DELETE", "/cart/coupon", undefined, ctx);
}

// ---------------------------------------------------------------------------
// POST /checkout/shipping-quote
// ---------------------------------------------------------------------------
//
// Contrat réel vérifié par test manuel de bout en bout côté apps/web : le
// corps ne contient QUE `districtId`, jamais `cartId`/`methodCodes`. Le panier
// est résolu côté serveur à partir de `X-Session-Token`/JWT, comme pour
// `/cart` ci-dessus.

export interface ShippingQuoteRequest {
  districtId: string;
}

export interface ShippingEstimatedDelivery {
  minAt?: string;
  maxAt?: string;
  label: string;
}

export interface ShippingOption {
  methodCode: string;
  label: string;
  price: MoneyAmount;
  originalPrice?: MoneyAmount;
  freeShippingApplied: boolean;
  freeShippingThreshold?: MoneyAmount;
  estimatedDelivery: ShippingEstimatedDelivery;
}

export interface ShippingQuoteResponse {
  zone: { id: string; name: string };
  options: ShippingOption[];
}

export function getShippingQuote(
  payload: ShippingQuoteRequest,
  ctx?: CartAuthContext | string | null,
): Promise<ShippingQuoteResponse> {
  return sessionApiFetch<ShippingQuoteResponse>("POST", "/checkout/shipping-quote", payload, ctx);
}

// ---------------------------------------------------------------------------
// POST /checkout/orders
// ---------------------------------------------------------------------------
//
// Contrat réel vérifié par test manuel de bout en bout côté apps/web : le
// corps est À PLAT, pas d'objet `shippingAddress` imbriqué, pas de `cartId`
// (panier résolu côté serveur), pas de `paymentMethod` (paiement à la
// livraison implicite au lot 1, RG/§8.2). Soit `addressId` (client
// authentifié, adresse déjà enregistrée), soit `districtId` +
// `recipientName` + `phoneE164` (obligatoires en commande invité).

export interface CreateOrderRequest {
  /** Adresse déjà enregistrée (client authentifié) ; sinon, les champs ci-dessous sont requis. */
  addressId?: string;
  districtId?: string;
  recipientName?: string;
  /** Format E.164, ex. "+2250700000000". */
  phoneE164?: string;
  quartier?: string;
  addressLine?: string;
  /** Repère de livraison, ex. « face à la pharmacie » — visible d'emblée (§8.2). */
  landmark?: string;
  /** Toujours requis. */
  shippingMethodCode: string;
  customerNote?: string;
}

export interface CreateOrderTotals {
  subtotal: MoneyAmount;
  discount: MoneyAmount;
  shipping: MoneyAmount;
  tax: MoneyAmount;
  grandTotal: MoneyAmount;
}

export interface CreateOrderResponse {
  orderNumber: string;
  status: string;
  paymentStatus: string;
  message: string;
  nextAction: { type: string; expectedWithinHours?: number };
  totals: CreateOrderTotals;
}

/**
 * @param idempotencyKey Généré une fois par le client (ex. `crypto.randomUUID()`)
 *   et réutilisé pour toute nouvelle tentative de la même commande, conformément
 *   à l'exigence d'idempotence de §11.1. Ne JAMAIS en régénérer un à chaque clic
 *   « Valider » : la même tentative doit toujours porter la même clé.
 * @param ctx Jeton de panier invité et/ou jeton d'accès client.
 */
export function createOrder(
  payload: CreateOrderRequest,
  idempotencyKey: string,
  ctx?: CartAuthContext | string | null,
): Promise<CreateOrderResponse> {
  return sessionApiFetch<CreateOrderResponse>("POST", "/checkout/orders", payload, ctx, {
    idempotencyKey,
  });
}

// ---------------------------------------------------------------------------
// POST /auth/otp/verify — POST /auth/refresh
// ---------------------------------------------------------------------------

/** Vue publique d'un client telle que renvoyée par les réponses d'auth. */
export interface AuthCustomer {
  id: string;
  phoneE164: string;
  firstName: string | null;
  lastName: string | null;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthResult extends AuthTokens {
  customer: AuthCustomer;
}

export interface VerifyOtpPayload {
  /** Numéro au format E.164, ex. "+2250700000000". */
  phoneE164: string;
  /** Code à 6 chiffres reçu par email (ou renvoyé directement par l'API en mode dev). */
  code: string;
}

export function verifyOtp(payload: VerifyOtpPayload): Promise<AuthResult> {
  return apiFetch<AuthResult>("POST", "/auth/otp/verify", payload, { cache: "no-store" });
}

export interface RefreshTokensPayload {
  refreshToken: string;
}

export function refreshTokens(payload: RefreshTokensPayload): Promise<AuthResult> {
  return apiFetch<AuthResult>("POST", "/auth/refresh", payload, { cache: "no-store" });
}

// ---------------------------------------------------------------------------
// GET /catalog/brands
// ---------------------------------------------------------------------------
//
// Forme confirmée par lecture de apps/api/src/catalog/catalog.service.ts
// (CatalogService.listBrands()). Nommé `CatalogBrandSummary` (et non
// `BrandSummary`, déjà pris plus haut par la forme imbriquée dans
// `ProductListItem`, qui n'expose pas `isFeatured`).

export interface CatalogBrandSummary {
  id: string;
  name: string;
  slug: string;
  countryOfOrigin: string | null;
  isFeatured: boolean;
  /** Logo de la marque, `null` si non renseigné. */
  logoUrl: string | null;
}

export function getCatalogBrands(): Promise<CatalogBrandSummary[]> {
  return apiFetch<CatalogBrandSummary[]>("GET", "/catalog/brands");
}

// ---------------------------------------------------------------------------
// GET /content/settings — `setting` marqués `isPublic = true` (§6 D10), ex.
// `support.whatsapp`/`support.phone`. Valeurs modifiables en base sans
// redéploiement (cf. src/lib/contact.ts pour l'usage côté contact/WhatsApp).
// ---------------------------------------------------------------------------

export function getPublicSettings(): Promise<Record<string, unknown>> {
  return apiFetch<Record<string, unknown>>("GET", "/content/settings");
}

// ---------------------------------------------------------------------------
// GET /search
// ---------------------------------------------------------------------------
//
// Forme confirmée par lecture directe de apps/api/src/search/search.controller.ts
// et search.service.ts : `SearchService.search()` renvoie
// `CursorPaginatedResponseDto<ProductListItemView> & { suggestions }`, soit
// exactement `{ items, nextCursor, suggestions }` — `items` a la même forme que
// `ProductListItem` ci-dessus.

export interface SearchSuggestion {
  label: string;
  slug: string;
  score: number;
}

export interface SearchResponse {
  items: ProductListItem[];
  nextCursor: string | null;
  suggestions: SearchSuggestion[];
}

/**
 * `q` est requis côté API (`SearchQueryDto.q` porte `@IsNotEmpty()` — un `q`
 * vide/absent renvoie 400) : n'appeler cette fonction qu'avec un terme non vide.
 */
export function searchProducts(params: { q: string; cursor?: string; limit?: number }): Promise<SearchResponse> {
  return apiFetch<SearchResponse>("GET", "/search", undefined, {
    searchParams: params,
    // Résultats dépendants de la requête utilisateur : jamais mis en cache.
    cache: "no-store",
  });
}

// ---------------------------------------------------------------------------
// GET /customers/me/wishlist · POST/DELETE /customers/me/wishlist/items
// ---------------------------------------------------------------------------
//
// Forme confirmée par lecture directe de apps/api/src/customer/customer.controller.ts,
// customer.service.ts et customer.types.ts : `WishlistItemView` remonte du
// `variantId` jusqu'au produit parent (nom, slug, image) puisque `WishlistItem`
// référence une variante précise, pas un produit. Ces trois routes exigent un
// client authentifié : `sessionApiFetch` uniquement pour son support déjà en
// place de l'en-tête `Authorization: Bearer` (désormais redondant avec le
// cookie httpOnly attaché par `credentials: "include"`, gardé pour signature
// stable — cf. commentaire de tête de fichier).

export interface WishlistItem {
  variantId: string;
  addedAt: string;
  note: string | null;
  productSlug: string;
  productName: string;
  /** Complément de nom de la variante (ex. "42 mm — Noir"), ou `null`. */
  variantNameSuffix: string | null;
  price: MoneyAmount;
  compareAtPrice: MoneyAmount | null;
  image: { url: string; alt: string | null } | null;
  inStock: boolean;
}

/** `GET /customers/me/wishlist` — crée la wishlist par défaut du client si besoin (get-or-create côté API). */
export function getWishlist(accessToken?: string): Promise<WishlistItem[]> {
  return sessionApiFetch<WishlistItem[]>("GET", "/customers/me/wishlist", undefined, { accessToken });
}

/** `POST /customers/me/wishlist/items` — idempotent (ajouter une variante déjà présente ne duplique rien). */
export function addWishlistItem(variantId: string, accessToken?: string): Promise<WishlistItem[]> {
  return sessionApiFetch<WishlistItem[]>("POST", "/customers/me/wishlist/items", { variantId }, { accessToken });
}

/** `DELETE /customers/me/wishlist/items/:variantId` — idempotent (retirer une variante absente n'est pas une erreur). */
export function removeWishlistItem(variantId: string, accessToken?: string): Promise<WishlistItem[]> {
  return sessionApiFetch<WishlistItem[]>(
    "DELETE",
    `/customers/me/wishlist/items/${encodeURIComponent(variantId)}`,
    undefined,
    { accessToken },
  );
}

// ---------------------------------------------------------------------------
// GET /orders (historique des commandes du client connecté)
// ---------------------------------------------------------------------------
//
// Forme confirmée par lecture directe de apps/api/src/order/order.service.ts
// `OrderService.findByCustomer()` : `CursorPaginatedResponseDto<{ orderNumber,
// status, paymentStatus, placedAt, itemsCount, total, thumbnailUrl }>`, soit
// `{ items, nextCursor }`. Route protégée par `JwtAuthGuard` — authentifiée ici
// via le cookie httpOnly (`credentials: "include"`) ; `accessToken` devenu
// optionnel (gardé pour signature stable, cf. commentaire de tête de fichier).

/** Résumé de commande pour un listing (pas le détail complet items/events). */
export interface OrderSummary {
  orderNumber: string;
  status: string;
  paymentStatus: string;
  /** ISO 8601. */
  placedAt: string;
  itemsCount: number;
  total: MoneyAmount;
  thumbnailUrl: string | null;
}

export interface OrderSummaryListResponse {
  items: OrderSummary[];
  nextCursor: string | null;
}

export async function getMyOrders(
  accessToken?: string,
  cursor?: string,
  limit?: number,
): Promise<OrderSummaryListResponse> {
  const requestPath = buildRequestPath("/orders", { cursor, limit });

  const headers: Record<string, string> = { Accept: "application/json" };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(requestPath, {
    method: "GET",
    headers,
    cache: "no-store",
    credentials: "include",
  });

  if (!res.ok) {
    let problem: ApiProblemDetails | undefined;
    try {
      problem = (await res.json()) as ApiProblemDetails;
    } catch {
      problem = undefined;
    }
    throw new ApiError(
      res.status,
      problem,
      problem?.detail ?? problem?.title ?? `Échec de la requête API VVS : GET /orders (HTTP ${res.status})`,
    );
  }

  return (await res.json()) as OrderSummaryListResponse;
}

// ---------------------------------------------------------------------------
// GET /orders/:orderNumber
// ---------------------------------------------------------------------------
//
// Client authentifié uniquement (pas de gestion du cas invité `?phone=` ici,
// hors périmètre de l'espace compte). Forme confirmée par lecture directe de
// apps/api/src/order/order.service.ts `OrderService.findByOrderNumber()`.

export interface OrderDetailShippingAddress {
  recipientName: unknown;
  addressLine: unknown;
  quartier: unknown;
  landmark: unknown;
}

export interface OrderDetailTotals {
  subtotal: MoneyAmount;
  discount: MoneyAmount;
  shipping: MoneyAmount;
  tax: MoneyAmount;
  grandTotal: MoneyAmount;
  paid: MoneyAmount;
  refunded: MoneyAmount;
}

export interface OrderDetailItem {
  sku: string;
  productName: string;
  variantName: string | null;
  imageUrl: string | null;
  quantity: number;
  fulfilledQuantity: number;
  returnedQuantity: number;
  unitPrice: MoneyAmount;
  lineTotal: MoneyAmount;
}

export interface OrderDetailEvent {
  type: string;
  fromStatus: string | null;
  toStatus: string | null;
  occurredAt: string;
  actorType: string;
}

export interface OrderDetail {
  orderNumber: string;
  status: string;
  paymentStatus: string;
  fulfillmentStatus: string;
  channel: string;
  placedAt: string;
  confirmedAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  shippingAddress: OrderDetailShippingAddress | null;
  totals: OrderDetailTotals;
  items: OrderDetailItem[];
  events: OrderDetailEvent[];
}

export async function getOrderDetail(orderNumber: string, accessToken?: string): Promise<OrderDetail> {
  const requestPath = buildRequestPath(`/orders/${encodeURIComponent(orderNumber)}`);

  const headers: Record<string, string> = { Accept: "application/json" };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(requestPath, {
    method: "GET",
    headers,
    cache: "no-store",
    credentials: "include",
  });

  if (!res.ok) {
    let problem: ApiProblemDetails | undefined;
    try {
      problem = (await res.json()) as ApiProblemDetails;
    } catch {
      problem = undefined;
    }
    throw new ApiError(
      res.status,
      problem,
      problem?.detail ??
        problem?.title ??
        `Échec de la requête API VVS : GET /orders/${orderNumber} (HTTP ${res.status})`,
    );
  }

  return (await res.json()) as OrderDetail;
}
